import React from 'react';
import styled from 'styled-components';
import { Answer } from './api';
import { FeedbackCommentProps, FeedbackSectionId } from './utils';
import {
  getTimePeriodText,
  IndicatorDetails,
  SectionIndicators,
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import { GroupedComments } from 'npo/pages/BetaFeedback/Feedback/ImpactDetailComments';
import { FeedbackCommentsSectionProps } from './FeedbackCommentsSection';
import {
  IndicatorByService,
  IndicatorSectionKey,
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { TrueImpactIndicatorFilesRender } from 'shared/design-system/components/TrueImpactFileRender';
import FeedbackModelComments from './FeedbackModelComments';
import { getIndicatorDetailsMeasurementKey } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/utils';
import { FeedbackApi } from '../api';
import { useIndicator } from 'shared/providers/IndicatorProvider';

interface RenderImpactIndicatorProps {
  answer: Answer;
  reportId: number;
  questionId: number;
  report: FeedbackApi;
  indicator: IndicatorDetails;
  sectionId: FeedbackSectionId;
  sectionKey: IndicatorSectionKey;
  otherDetails: SectionIndicators;
  impactDetails: SectionIndicators;
  impactSummary: IndicatorByService;
  groupedComments: GroupedComments;
  comments: FeedbackCommentProps[];
  onSuccessSave: FeedbackCommentsSectionProps['onSuccessSave'];
  onSuccessDelete: FeedbackCommentsSectionProps['onSuccessDelete'];
  addOrUpdateComment: FeedbackCommentsSectionProps['addOrUpdateComment'];
  deleteQuestionLevelComment: FeedbackCommentsSectionProps['deleteQuestionLevelComment'];
  updateQuestionLevelComment: FeedbackCommentsSectionProps['updateQuestionLevelComment'];
}

const StyledDiv = styled.div`
  padding: ${SPACERS.medium};
  border-radius: ${SPACERS.tiny};
  border: 1px solid ${COLORS.grayLight};
  background-color: ${COLORS.grayLightest};
  .indicator-label {
    margin-bottom: ${SPACERS.extraTiny};
  }
  .indicator-value {
    margin-bottom: ${SPACERS.small};
  }
  .show-indicator-comments {
    justify-content: center;
  }
  .indicator-comments {
    margin-top: ${SPACERS.tiny};
  }
`;

const RenderImpactIndicator: React.FC<RenderImpactIndicatorProps> = ({
  report,
  answer,
  comments,
  reportId,
  questionId,
  indicator,
  sectionId,
  sectionKey,
  otherDetails,
  impactSummary,
  onSuccessSave,
  onSuccessDelete,
  addOrUpdateComment,
  deleteQuestionLevelComment,
  updateQuestionLevelComment,
}) => {
  const { getSourcesAndAssumptions } = useIndicator();
  const i = impactSummary[sectionKey]?.find(
    x => x.id === indicator.indicatorId,
  );
  // const other = otherDetails[sectionKey]?.find(
  //   x => x.indicatorId === indicator.indicatorId,
  // );
  const measurementType = getIndicatorDetailsMeasurementKey(
    sectionKey,
    indicator.measurement,
  );
  const description = getSourcesAndAssumptions(indicator);
  // const otherDescription = other ? getSourcesAndAssumptions(other) : undefined;
  const details = { sectionId: sectionKey, indicatorId: indicator.indicatorId };

  return (
    <StyledDiv>
      <TrueImpactText
        variant="heading-label"
        color="blackBase"
        className="indicator-label"
      >
        INDICATOR
      </TrueImpactText>
      <TrueImpactText className="indicator-value">
        {numberWithCommas(
          indicator.finalForecast || indicator.forecast,
          // || other?.forecast ||
          // other?.finalForecast,
        )}{' '}
        {indicator.indicatorName}
        {/* {indicator.indicatorName || other?.indicatorName} */}
      </TrueImpactText>
      <TrueImpactText
        variant="heading-label"
        color="blackBase"
        className="indicator-label"
      >
        Definition
      </TrueImpactText>
      <TrueImpactText className="indicator-value">
        {i?.updatedText || 'Not provided'}
      </TrueImpactText>
      {indicator.timePeriod && (
        <>
          <TrueImpactText
            variant="heading-label"
            color="blackBase"
            className="indicator-label"
          >
            Impact horizon
          </TrueImpactText>
          <TrueImpactText className="indicator-value">
            {getTimePeriodText(
              indicator.timePeriod,
              // indicator.timePeriod || other?.timePeriod,
              report.startDate,
              report.endDate,
            )}
          </TrueImpactText>
        </>
      )}
      <TrueImpactText
        variant="heading-label"
        color="blackBase"
        className="indicator-label"
      >
        MEASUREMENT
      </TrueImpactText>
      <TrueImpactText className="indicator-value">
        {measurementType ? `${measurementType} - ` : ''}
        {indicator.measurement || 'Not provided'}
        {/* {indicator.measurement || other?.measurement} */}
      </TrueImpactText>
      <TrueImpactText
        variant="heading-label"
        color="blackBase"
        className="indicator-label"
      >
        Sources and Assumptions
      </TrueImpactText>
      <TrueImpactText className="indicator-value">
        {description || 'Not provided'}
        {/* {description || otherDescription || 'Not provided'} */}
      </TrueImpactText>
      <TrueImpactIndicatorFilesRender indicator={indicator} />
      <FeedbackModelComments
        comments={comments}
        reportId={reportId}
        questionId={questionId}
        sectionId={sectionId}
        answer={answer}
        details={JSON.stringify(details)}
        nullFeedbackText="Add Feedback For This Indicator"
        hideModelButton={true}
        showWrapperClassName="show-indicator-comments"
        commentsWrapperClassName="indicator-comments"
        textareaWrapperClassName="indicator-comments-textarea"
        deleteQuestionLevelComment={deleteQuestionLevelComment}
        updateQuestionLevelComment={updateQuestionLevelComment}
        onSuccessDelete={onSuccessDelete}
        onSuccessSave={onSuccessSave}
        addOrUpdateComment={addOrUpdateComment}
      />
    </StyledDiv>
  );
};

export default RenderImpactIndicator;
